import { useEffect, useMemo } from "react";
import { useStore } from "zustand";
import { Product, FeatureFlags } from "@/types/types";
import { Services } from "@/types/serviceTypes";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { mappedServices } from "@/utils/services/serviceMapper";
import { getBundleServices } from "@/utils/product/getBundleServices";
import { sharedServicesStore } from "@/utils/services/sharedServicesStore";

export type SharedServicesParams = {
  product: Product;
  flags: Pick<FeatureFlags, "mandatoryServicesOverride">;
};

export type SharedServicesActions = {
  selectService: (serviceId: string) => void;
  removeService: (serviceId: string) => void;
};

export type SharedServicesResult = {
  availableServices: Services;
  selectedServiceIds: ReadonlySet<string>;
  mandatoryServiceIds: ReadonlySet<string>;
  effectiveSelectedServiceIds: ReadonlySet<string>;
  effectiveSelectedServices: Services;
  actions: SharedServicesActions;
};

export function useSharedServices({
  product,
  flags,
}: SharedServicesParams): SharedServicesResult {
  const { variant } = useVariant(product);

  const mandatoryServicesOverrideStr =
    flags.mandatoryServicesOverride.value.toString();

  const availableServicesForVariant = useMemo(() => {
    let variantServices = variant?.services;
    if (product.__typename === "CompositeBundle") {
      variantServices = getBundleServices(variant);
    }
    return mappedServices(variantServices, {
      mandatoryServicesOverride: mandatoryServicesOverrideStr.split(","),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant?.id, mandatoryServicesOverrideStr, product.__typename]);

  const variantId = variant?.id;
  const syncKey = variantId ?? product?.aliases?.pimProductId;

  useEffect(() => {
    if (!syncKey) return;
    if (!variantId && availableServicesForVariant.length === 0) return;
    sharedServicesStore
      .getState()
      .syncForVariant(syncKey, availableServicesForVariant);
  }, [syncKey, variantId, availableServicesForVariant]);

  const availableServices = useStore(
    sharedServicesStore,
    (s) => s.availableServices,
  );

  const selectedServiceIds = useMemo(
    () =>
      new Set(
        availableServices
          .filter((s) => s.selected && !s.isMandatory)
          .map((s) => s.id),
      ),
    [availableServices],
  );

  const mandatoryServiceIds = useMemo(
    () =>
      new Set(availableServices.filter((s) => s.isMandatory).map((s) => s.id)),
    [availableServices],
  );

  const effectiveSelectedServiceIds = useMemo(
    () =>
      new Set(
        availableServices
          .filter((s) => s.selected || s.isMandatory)
          .map((s) => s.id),
      ),
    [availableServices],
  );

  const effectiveSelectedServices = useMemo(
    () => availableServices.filter((s) => s.selected || s.isMandatory),
    [availableServices],
  );

  const actions: SharedServicesActions = useMemo(
    () => ({
      selectService: (serviceId: string) =>
        sharedServicesStore.getState().selectService(serviceId),
      removeService: (serviceId: string) =>
        sharedServicesStore.getState().removeService(serviceId),
    }),
    [],
  );

  return {
    availableServices,
    selectedServiceIds,
    mandatoryServiceIds,
    effectiveSelectedServiceIds,
    effectiveSelectedServices,
    actions,
  };
}

export function useSharedServicesState(): {
  selectedServicesPrice: number;
  selectedServicesCount: number;
} {
  const availableServices = useStore(
    sharedServicesStore,
    (s) => s.availableServices,
  );

  const selectedServicesPrice = useMemo(
    () =>
      availableServices
        .filter((s) => s.selected || s.isMandatory)
        .reduce((acc, { value }) => acc + value, 0),
    [availableServices],
  );

  const selectedServicesCount = useMemo(
    () => availableServices.filter((s) => s.selected || s.isMandatory).length,
    [availableServices],
  );

  return { selectedServicesPrice, selectedServicesCount };
}

export const resetSharedServices = () => {
  sharedServicesStore.getState().reset();
};
